import { useCallback, useEffect, useState } from 'react';
import { notify } from '@/lib/notify';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  listAllSubscriptions, formatCAD, daysUntil,
  adminExtendTrial, adminSetPilot, adminCompActive,
  adminMarkPastDue, adminCancelSubscription, adminSetInternalNote,
  type AdminSubscriptionRow,
} from '@/lib/adminBilling';
import { listCoupons, applyDiscount, removeDiscount, describeCoupon, type Coupon } from '@/lib/promotions';
import { humanize } from '@/lib/errors';
import { formatDate } from '@/lib/dates';

/**
 * /admin/subscriptions/:orgId — single subscription editor.
 *
 * All actions go through SECURITY DEFINER RPCs. Each action requires a
 * reason which gets recorded in audit_logs.
 */
export default function SubscriptionDetail() {
  const navigate = useNavigate();
  const { orgId } = useParams<{ orgId: string }>();
  const [row, setRow] = useState<AdminSubscriptionRow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // After any action handler succeeds, return the admin to /admin/subscriptions.
  // The action handler itself fires the toast first; we just navigate.
  const onActionDone = useCallback(() => {
    navigate('/admin/subscriptions');
  }, [navigate]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const all = await listAllSubscriptions();
      const r = all.find((x) => x.organization_id === orgId) ?? null;
      setRow(r);
    } catch (err) {
      setError(humanize(err));
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { refresh(); }, [refresh]);

  if (loading) return <p className="text-sm text-muted-foreground">Loading subscription…</p>;
  if (error)   return <p className="text-sm text-red-600">{error}</p>;
  if (!row)    return <p className="text-sm text-muted-foreground">Subscription not found.</p>;

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <Link to="/admin/subscriptions" className="text-xs underline text-muted-foreground">← All subscriptions</Link>
        <h2 className="text-2xl font-semibold tracking-tight">{row.org_display_name}</h2>
        <p className="text-sm text-muted-foreground">{row.primary_contact_email ?? '—'} · joined {formatDate(row.org_created_at)}</p>
      </header>

      <CurrentState row={row} />

      <section className="grid gap-4 lg:grid-cols-2">
        <ExtendTrialAction orgId={row.organization_id} onDone={onActionDone} />
        <SetPilotAction orgId={row.organization_id} onDone={onActionDone} />
        <CompActiveAction orgId={row.organization_id} onDone={onActionDone} />
        <ApplyDiscountAction row={row} onDone={onActionDone} />
        <NoteAction orgId={row.organization_id} current={row.internal_note} onDone={onActionDone} />
      </section>

      <DangerZone orgId={row.organization_id} onDone={onActionDone} />
    </div>
  );
}

function CurrentState({ row }: { row: AdminSubscriptionRow }) {
  const days = row.status === 'trial'
    ? daysUntil(row.trial_ends_at)
    : row.status === 'pilot'
      ? daysUntil(row.pilot_ends_at)
      : row.status === 'active'
        ? daysUntil(row.current_period_end)
        : null;

  return (
    <section className="rounded-lg border border-border bg-card p-4 space-y-3">
      <h3 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">Current state</h3>
      <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
        <Stat label="Status" value={row.status ?? '—'} />
        <Stat label="Tier" value={row.tier ? row.tier[0].toUpperCase() + row.tier.slice(1) : '—'} />
        <Stat label="Billing" value={row.billing_period ?? '—'} />
        <Stat label="Days remaining" value={days === null ? '—' : `${days}d`} />
        <Stat label="Locations" value={String(row.active_location_count)} />
        <Stat label="Staff" value={String(row.staff_count)} />
        <Stat label="Rate" value={Number(row.monthly_rate_cad) === 0 ? '—' : `${formatCAD(Number(row.monthly_rate_cad))}/loc/mo`} />
        <Stat label="MRR" value={formatCAD(Number(row.mrr_cad))} />
      </dl>
      {row.internal_note && (
        <p className="text-xs text-muted-foreground italic">Note: {row.internal_note}</p>
      )}
    </section>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd className="text-sm font-medium" style={{ color: 'hsl(var(--brand-navy))' }}>{value}</dd>
    </div>
  );
}

function ExtendTrialAction({ orgId, onDone }: { orgId: string; onDone: () => void }) {
  const [days, setDays] = useState(14);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function go() {
    setBusy(true); setErr(null);
    try {
      await adminExtendTrial(orgId, days, reason || null);
      // Fire toast BEFORE onDone() — the refresh re-renders the page and
      // can swallow a toast queued after it.
      notify.success(`Trial extended by ${days} day${days === 1 ? '' : 's'}`);
      setReason(''); setDays(14);
      onDone();
    } catch (e) { setErr(humanize(e)); }
    finally { setBusy(false); }
  }

  return (
    <ActionCard title="Extend trial" hint="Adds days to trial_ends_at. Resets canceled/past_due back to trial.">
      <div className="flex gap-2 items-center">
        <input type="number" min={1} max={365} value={days} onChange={(e) => setDays(Number(e.target.value))}
               className="w-24 rounded-md border border-border px-2 py-1 text-sm" />
        <span className="text-xs text-muted-foreground">days</span>
      </div>
      <ReasonInput value={reason} onChange={setReason} />
      {err && <p className="text-xs text-red-600">{err}</p>}
      <button disabled={busy} onClick={go} className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-50">
        {busy ? 'Working…' : `Extend by ${days} days`}
      </button>
    </ActionCard>
  );
}

function SetPilotAction({ orgId, onDone }: { orgId: string; onDone: () => void }) {
  const [days, setDays] = useState(90);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function go() {
    setBusy(true); setErr(null);
    try {
      await adminSetPilot(orgId, days, reason || null);
      notify.success(`Set to pilot for ${days} day${days === 1 ? '' : 's'}`);
      setReason(''); setDays(90);
      onDone();
    } catch (e) { setErr(humanize(e)); }
    finally { setBusy(false); }
  }

  return (
    <ActionCard title="Flip to pilot" hint="Founding-cohort program: free until pilot_ends_at.">
      <div className="flex gap-2 items-center">
        <input type="number" min={1} max={365} value={days} onChange={(e) => setDays(Number(e.target.value))}
               className="w-24 rounded-md border border-border px-2 py-1 text-sm" />
        <span className="text-xs text-muted-foreground">days from now</span>
      </div>
      <ReasonInput value={reason} onChange={setReason} />
      {err && <p className="text-xs text-red-600">{err}</p>}
      <button disabled={busy} onClick={go} className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-50">
        {busy ? 'Working…' : `Set pilot · ${days} days`}
      </button>
    </ActionCard>
  );
}

function CompActiveAction({ orgId, onDone }: { orgId: string; onDone: () => void }) {
  const [tier, setTier] = useState<'standard'|'pro'|'enterprise'>('standard');
  const [period, setPeriod] = useState<'monthly'|'annual'>('monthly');
  const [endDate, setEndDate] = useState(() => {
    const d = new Date(); d.setMonth(d.getMonth()+1); return d.toISOString().slice(0,10);
  });
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function go() {
    setBusy(true); setErr(null);
    try {
      const iso = new Date(endDate + 'T23:59:59Z').toISOString();
      await adminCompActive(orgId, tier, period, iso, reason || null);
      notify.success(`Comped to active (${tier}, ${period}) through ${endDate}`);
      setReason('');
      onDone();
    } catch (e) { setErr(humanize(e)); }
    finally { setBusy(false); }
  }

  return (
    <ActionCard title="Comp to active (no Stripe)" hint="Manual relationship deal. Sets tier + billing period without payment.">
      <div className="grid grid-cols-2 gap-2">
        <label className="text-xs space-y-1">
          <span className="text-muted-foreground uppercase tracking-wider">Tier</span>
          <select value={tier} onChange={(e)=>setTier(e.target.value as 'standard' | 'pro' | 'enterprise')} className="w-full rounded-md border border-border px-2 py-1 text-sm">
            <option value="standard">Standard</option>
            <option value="pro">Pro</option>
            <option value="enterprise">Enterprise</option>
          </select>
        </label>
        <label className="text-xs space-y-1">
          <span className="text-muted-foreground uppercase tracking-wider">Billing</span>
          <select value={period} onChange={(e)=>setPeriod(e.target.value as 'monthly' | 'annual')} className="w-full rounded-md border border-border px-2 py-1 text-sm">
            <option value="monthly">Monthly</option>
            <option value="annual">Annual</option>
          </select>
        </label>
      </div>
      <label className="text-xs space-y-1">
        <span className="text-muted-foreground uppercase tracking-wider">Period end</span>
        <input type="date" value={endDate} onChange={(e)=>setEndDate(e.target.value)} className="w-full rounded-md border border-border px-2 py-1 text-sm" />
      </label>
      <ReasonInput value={reason} onChange={setReason} />
      {err && <p className="text-xs text-red-600">{err}</p>}
      <button disabled={busy} onClick={go} className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-50">
        {busy ? 'Working…' : 'Comp to active'}
      </button>
    </ActionCard>
  );
}

function NoteAction({ orgId, current, onDone }: { orgId: string; current: string | null; onDone: () => void }) {
  const [note, setNote] = useState(current ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function go() {
    setBusy(true); setErr(null);
    try {
      await adminSetInternalNote(orgId, note);
      notify.success(note.trim() ? 'Internal note saved' : 'Internal note cleared');
      onDone();
    } catch (e) { setErr(humanize(e)); }
    finally { setBusy(false); }
  }
  return (
    <ActionCard title="Internal note" hint="Free-form note shown to the owner on /billing. Use for: 'comped per Q4 deal', 'paused while migrating data', etc.">
      <textarea value={note} onChange={(e)=>setNote(e.target.value)}
        rows={3} className="w-full rounded-md border border-border px-2 py-1 text-sm" />
      {err && <p className="text-xs text-red-600">{err}</p>}
      <button disabled={busy} onClick={go} className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-50">
        {busy ? 'Working…' : (note.trim() ? 'Save note' : 'Clear note')}
      </button>
    </ActionCard>
  );
}

function ApplyDiscountAction({ row, onDone }: { row: AdminSubscriptionRow; onDone: () => void }) {
  const [mode, setMode] = useState<'percent'|'amount'>('percent');
  const [pct, setPct] = useState('20');
  const [amount, setAmount] = useState('30');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [couponId, setCouponId] = useState<string>('');
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const orgId = row.organization_id;
  const hasDiscount = row.discount_pct != null || row.discount_amount_off_cad != null;

  useEffect(() => {
    let cancelled = false;
    listCoupons().then((c) => { if (!cancelled) setCoupons(c.filter((x)=>x.active)); }).catch(()=>{});
    return () => { cancelled = true; };
  }, []);

  // When admin picks a coupon, auto-fill mode + value from the coupon row.
  useEffect(() => {
    if (!couponId) return;
    const c = coupons.find((x) => x.id === couponId);
    if (!c) return;
    if (c.discount_type === 'percent') {
      setMode('percent');
      setPct(String(c.discount_value));
    } else {
      setMode('amount');
      setAmount(String(c.discount_value));
    }
  }, [couponId, coupons]);

  async function apply() {
    setBusy(true); setErr(null);
    try {
      // toast fires after the await resolves below
      await applyDiscount({
        orgId,
        discountPct: mode === 'percent' ? Number(pct) : null,
        discountAmountOffCad: mode === 'amount' ? Number(amount) : null,
        discountEndsAtIso: endDate ? new Date(endDate + 'T23:59:59Z').toISOString() : null,
        reason: reason.trim(),
        couponId: couponId || null,
      });
      notify.success('Discount applied'); onDone();;
    } catch (e) { setErr(humanize(e)); }
    finally { setBusy(false); }
  }
  async function remove() {
    if (!confirm('Remove the current discount from this subscription?')) return;
    setBusy(true); setErr(null);
    try { await removeDiscount(orgId, reason || 'cleared via cockpit'); notify.info('Discount removed'); onDone();; }
    catch (e) { setErr(humanize(e)); }
    finally { setBusy(false); }
  }

  return (
    <ActionCard title="Apply discount / coupon" hint="Pct off OR fixed amount off, with optional end date. Optionally link to a coupon (audited as a redemption).">
      {hasDiscount && (
        <div className="rounded-md border p-2 text-xs"
          style={{ borderColor: 'hsl(var(--brand-teal))', backgroundColor: 'hsl(var(--brand-teal) / 0.06)' }}>
          <strong>
            {row.discount_pct != null ? `${row.discount_pct}% off` : `$${row.discount_amount_off_cad} CAD off`}
          </strong>{' '}
          {row.discount_ends_at ? `until ${new Date(row.discount_ends_at).toLocaleDateString()}` : 'applied'}
          {row.applied_coupon_code && <span className="text-muted-foreground"> · coupon {row.applied_coupon_code}</span>}
          <button onClick={remove} className="ml-2 underline text-red-700">Remove</button>
        </div>
      )}
      <div className="flex gap-2 text-xs">
        <label><input type="radio" checked={mode==='percent'} onChange={()=>setMode('percent')} /> Percent</label>
        <label><input type="radio" checked={mode==='amount'} onChange={()=>setMode('amount')} /> Amount (CAD)</label>
      </div>
      {mode === 'percent' ? (
        <input type="number" min={1} max={100} value={pct} onChange={(e)=>setPct(e.target.value)}
          className="w-24 rounded-md border border-border px-2 py-1 text-sm" />
      ) : (
        <input type="number" min={0} step="0.01" value={amount} onChange={(e)=>setAmount(e.target.value)}
          className="w-32 rounded-md border border-border px-2 py-1 text-sm" />
      )}
      <label className="text-xs space-y-1 block">
        <span className="text-muted-foreground uppercase tracking-wider">End date (blank = forever)</span>
        <input type="date" value={endDate} onChange={(e)=>setEndDate(e.target.value)}
          className="w-full rounded-md border border-border px-2 py-1 text-sm" />
      </label>
      <label className="text-xs space-y-1 block">
        <span className="text-muted-foreground uppercase tracking-wider">Link to coupon (optional)</span>
        <select value={couponId} onChange={(e)=>setCouponId(e.target.value)}
          className="w-full rounded-md border border-border px-2 py-1 text-sm">
          <option value="">— none —</option>
          {coupons.map((c) => (
            <option key={c.id} value={c.id}>{c.code} — {describeCoupon(c)}</option>
          ))}
        </select>
      </label>
      <ReasonInput value={reason} onChange={setReason} />
      {err && <p className="text-xs text-red-600">{err}</p>}
      <button disabled={busy} onClick={apply} className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-50">
        {busy ? 'Working…' : 'Apply discount'}
      </button>
    </ActionCard>
  );
}

function DangerZone({ orgId, onDone }: { orgId: string; onDone: () => void }) {
  return (
    <section className="rounded-lg border-2 border-red-200 bg-red-50/40 p-4 space-y-3">
      <h3 className="text-sm font-medium uppercase tracking-wider text-red-900">Danger zone</h3>
      <div className="grid gap-3 sm:grid-cols-2">
        <PastDueAction orgId={orgId} onDone={onDone} />
        <CancelAction orgId={orgId} onDone={onDone} />
      </div>
    </section>
  );
}

function PastDueAction({ orgId, onDone }: { orgId: string; onDone: () => void }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function go() {
    if (!confirm('Mark this subscription past due?')) return;
    setBusy(true); setErr(null);
    try { await adminMarkPastDue(orgId, reason || null); notify.info('Marked past due'); onDone();; }
    catch (e) { setErr(humanize(e)); }
    finally { setBusy(false); }
  }
  return (
    <div className="rounded-md border border-red-200 bg-card p-3 space-y-2">
      <h4 className="text-sm font-medium">Mark past due</h4>
      <ReasonInput value={reason} onChange={setReason} />
      {err && <p className="text-xs text-red-600">{err}</p>}
      <button disabled={busy} onClick={go} className="rounded-md border-2 border-red-300 px-3 py-1.5 text-sm font-medium text-red-900 hover:bg-red-50 disabled:opacity-50">
        {busy ? 'Working…' : 'Mark past due'}
      </button>
    </div>
  );
}

function CancelAction({ orgId, onDone }: { orgId: string; onDone: () => void }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function go() {
    if (!confirm('Cancel this subscription? Account becomes read-only at end of period.')) return;
    setBusy(true); setErr(null);
    try { await adminCancelSubscription(orgId, reason || null); notify.info('Subscription cancelled'); onDone();; }
    catch (e) { setErr(humanize(e)); }
    finally { setBusy(false); }
  }
  return (
    <div className="rounded-md border border-red-200 bg-card p-3 space-y-2">
      <h4 className="text-sm font-medium">Cancel subscription</h4>
      <ReasonInput value={reason} onChange={setReason} />
      {err && <p className="text-xs text-red-600">{err}</p>}
      <button disabled={busy} onClick={go} className="rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50">
        {busy ? 'Working…' : 'Cancel subscription'}
      </button>
    </div>
  );
}

function ActionCard({ title, hint, children }: { title: string; hint: string; children: React.ReactNode }) {
  return (
    <section className="rounded-lg border border-border bg-card p-4 space-y-2">
      <header>
        <h3 className="text-sm font-medium" style={{ color: 'hsl(var(--brand-navy))' }}>{title}</h3>
        <p className="text-xs text-muted-foreground">{hint}</p>
      </header>
      <div className="space-y-2">{children}</div>
    </section>
  );
}

function ReasonInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <label className="block text-xs space-y-1">
      <span className="text-muted-foreground uppercase tracking-wider">Reason (audit-logged)</span>
      <input type="text" value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md border border-border px-2 py-1 text-sm"
        placeholder="e.g., extended for pilot kickoff" />
    </label>
  );
}
